import {
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Box,
  FormHelperText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import FileUpload from "../../GovernanceDashboard/DataEntry/FileUpload";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import styles from "../../../styles/social/social.module.css";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { get, put } from "../../../utils/api";
import MainDashboardLayout from "../../../Layout/MainDashboardLayout";
import SocialLayout from "../../../Layout/SocialLayout";
import { textError } from "../../../utils/validationMessage";
import WestIcon from "@mui/icons-material/West";
import DeleteModal from "../../../Components/Common/DeleteModal";

const SOCIAL_PRIMARY = "#1C486B";
const socialRadioSx = {
  color: SOCIAL_PRIMARY,
  "&.Mui-checked": { color: SOCIAL_PRIMARY },
};
const backButtonSx = {
  color: SOCIAL_PRIMARY,
  textTransform: "none",
  border: `1px solid ${SOCIAL_PRIMARY}`,
  "&:hover": { borderColor: SOCIAL_PRIMARY },
};
const primaryButtonSx = {
  textTransform: "none",
  backgroundColor: SOCIAL_PRIMARY,
  "&:hover": { backgroundColor: "#163a56" },
};

const NumericInputField = ({
  label,
  name,
  value,
  onChange,
  placeholder,
  errors,
}) => (
  <div style={{ marginBottom: "1rem" }}>
    <p className={styles.form_heading} style={{ fontWeight: "bold" }}>
      {label}
    </p>
    <TextField
      name={name}
      value={value}
      onChange={onChange}
      variant="outlined"
      size="small"
      sx={{ 
        pl: 2, 
        width: "50%",
        "& .MuiOutlinedInput-notchedOutline": {
          border: "none",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          border: "none",
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          border: "none",
        },
      }}
      placeholder={placeholder}
      error={errors?.[name]}
      helperText={errors?.[name]}
      FormHelperTextProps={{
        sx: {
          color: "#D34040", 
          marginLeft: "0rem", 
          fontSize: "14px",
          paddingLeft: "1px",
        },
      }}
    />
  </div>
);

const BooleanInputField = ({ label, name, value, onChange }) => (
  <FormControl component="fieldset" style={{ marginBottom: "1rem" }}>
    <p className={styles.form_heading} style={{ fontWeight: "bold" }}>
      {label}
    </p>
    <RadioGroup
      row
      name={name}
      value={value === false || value === "false" ? "false" : "true"}
      onChange={onChange}
      sx={{ pl: 2 }}
    >
      <FormControlLabel value="true" control={<Radio sx={socialRadioSx} />} label="Yes" />
      <FormControlLabel value="false" control={<Radio sx={socialRadioSx} />} label="No" />
    </RadioGroup>
  </FormControl>
);

const TrainingAndDevelopment = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [errors, setErrors] = useState({});
  const [module, setModule] = useState("monthly");
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteData, setDeleteData] = useState({ fieldName: "", index: "" });
  const [isRestricted, setIsRestricted] = useState(false);
  const [totalEmployees, setTotalEmployees] = useState(0);

  const formatWithCommas = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    const raw = String(value).replace(/,/g, "");
    if (!/^\d+$/.test(raw)) return "";
    return Number(raw).toLocaleString("en-IN");
  };

  const stripCommasFromNumericStrings = (input) => {
    if (Array.isArray(input)) return input.map(stripCommasFromNumericStrings);
    if (input && typeof input === "object") {
      return Object.fromEntries(
        Object.entries(input).map(([key, value]) => [
          key,
          stripCommasFromNumericStrings(value),
        ]),
      );
    }
    if (typeof input === "string" && /^\d{1,3}(,\d{3})*(\.\d+)?$/.test(input)) {
      return input.replace(/,/g, "");
    }
    return input;
  };

  // Default skill training topics that should always be present
  const defaultSkillTrainingTopics = [
    { topic: "Business Ethics & Code of Conduct", employeesParticipated: "", percentage: "", documentUrl: "" },
    { topic: "Integrity & Ethical Decision-Making", employeesParticipated: "", percentage: "", documentUrl: "" },
    { topic: "Anti-Bribery & Anti-Corruption Awareness", employeesParticipated: "", percentage: "", documentUrl: "" },
    { topic: "Conflict of Interest Awareness", employeesParticipated: "", percentage: "", documentUrl: "" },
    { topic: "Whistle-blower & Grievance Redressal Mech", employeesParticipated: "", percentage: "", documentUrl: "" },
    { topic: "Regulatory & Legal Compliance Awareness", employeesParticipated: "", percentage: "", documentUrl: "" },
    { topic: "ESG & Responsible Business Conduct Aware", employeesParticipated: "", percentage: "", documentUrl: "" },
    { topic: "Productivity & Process Improvement", employeesParticipated: "", percentage: "", documentUrl: "" },
    { topic: "Career Development & Capability Building", employeesParticipated: "", percentage: "", documentUrl: "" },
    { topic: "Human Rights Policy Awareness", employeesParticipated: "", percentage: "", documentUrl: "" },
    { topic: "Prevention of Child Labour", employeesParticipated: "", percentage: "", documentUrl: "" },
  ];

  // Default awareness training topics that should always be present
  const defaultAwarenessTrainingTopics = [
    { topic: "Occupational Health & Safety (OHS)", employeesParticipated: "", percentage: "", documentUrl: "" },
    { topic: "Workplace Hazard Identification & Risk Assessment", employeesParticipated: "", percentage: "", documentUrl: "" },
    { topic: "Fire Safety & Emergency Preparedness", employeesParticipated: "", percentage: "", documentUrl: "" },
    { topic: "First Aid & Accident Response", employeesParticipated: "", percentage: "", documentUrl: "" },
    { topic: "Use of Personal Protective Equipment (PPE)", employeesParticipated: "", percentage: "", documentUrl: "" },
    { topic: "Incident Reporting & Safety Compliance", employeesParticipated: "", percentage: "", documentUrl: "" },
    { topic: "Technical Skill Development", employeesParticipated: "", percentage: "", documentUrl: "" },
    { topic: "Functional & Role-based Training", employeesParticipated: "", percentage: "", documentUrl: "" },
    { topic: "Environmental Management Awareness", employeesParticipated: "", percentage: "", documentUrl: "" },
    { topic: "Energy Conservation & Efficiency", employeesParticipated: "", percentage: "", documentUrl: "" },
    { topic: "Water Conservation & Stewardship", employeesParticipated: "", percentage: "", documentUrl: "" },
    { topic: "Waste Management & Segregation", employeesParticipated: "", percentage: "", documentUrl: "" },
    { topic: "Hazardous Waste Handling", employeesParticipated: "", percentage: "", documentUrl: "" },
    { topic: "Environmental Legal Compliance", employeesParticipated: "", percentage: "", documentUrl: "" },
    { topic: "Pollution Prevention Awareness", employeesParticipated: "", percentage: "", documentUrl: "" },
    { topic: "Product Safety & Quality Awareness", employeesParticipated: "", percentage: "", documentUrl: "" },
    { topic: "Safe & Responsible Product Usage", employeesParticipated: "", percentage: "", documentUrl: "" },
  ];
  // Annual formData
  const annualFormDataInitial = {
    programsOffered: {
      status: false,
    },
    budgetAllocated: { currency: "", amount: "" },
    skillTrainings: [
      {
        letter: "a",
        trainings: [
          { topic: "Business Ethics & Code of Conduct", employeesParticipated: "", percentage: "", documentUrl: "" },
          { topic: "Integrity & Ethical Decision-Making", employeesParticipated: "", percentage: "", documentUrl: "" },
          { topic: "Anti-Bribery & Anti-Corruption Awareness", employeesParticipated: "", percentage: "", documentUrl: "" },
          { topic: "Conflict of Interest Awareness", employeesParticipated: "", percentage: "", documentUrl: "" },
          { topic: "Whistle-blower & Grievance Redressal Mech", employeesParticipated: "", percentage: "", documentUrl: "" },
          { topic: "Regulatory & Legal Compliance Awareness", employeesParticipated: "", percentage: "", documentUrl: "" },
          { topic: "ESG & Responsible Business Conduct Aware", employeesParticipated: "", percentage: "", documentUrl: "" },
          { topic: "Productivity & Process Improvement", employeesParticipated: "", percentage: "", documentUrl: "" },
          { topic: "Career Development & Capability Building", employeesParticipated: "", percentage: "", documentUrl: "" },
          { topic: "Human Rights Policy Awareness", employeesParticipated: "", percentage: "", documentUrl: "" },
        ],
      },
    ],
    awarenessTrainings: [
      {
        letter: "b",
        trainings: [
          { topic: "Occupational Health & Safety (OHS)", employeesParticipated: "", percentage: "", documentUrl: "" },
          { topic: "Workplace Hazard Identification & Risk Assessment", employeesParticipated: "", percentage: "", documentUrl: "" },
          { topic: "Fire Safety & Emergency Preparedness", employeesParticipated: "", percentage: "", documentUrl: "" },
          { topic: "First Aid & Accident Response", employeesParticipated: "", percentage: "", documentUrl: "" },
          { topic: "Use of Personal Protective Equipment (PPE)", employeesParticipated: "", percentage: "", documentUrl: "" },
          { topic: "Incident Reporting & Safety Compliance", employeesParticipated: "", percentage: "", documentUrl: "" },
        ],
      },
    ],
  };

  // Monthly formData
  const monthlyFormDataInitial = {
    programsOffered: {
      status: true,
      noReason: "",
    },
    budgetAllocated: { currency: "", amount: "" },
  
    skillTrainings: {
      trainings: defaultSkillTrainingTopics.map(topic => ({ ...topic })),
    },
    awarenessTrainings: {
      trainings: defaultAwarenessTrainingTopics.map(topic => ({ ...topic })),
    },
  };

  const [annualFormData, setAnnualFormData] = useState(annualFormDataInitial);
  const [monthlyFormData, setMonthlyFormData] = useState(monthlyFormDataInitial);

  // This component is monthly-only

  const [newTopic, setNewTopic] = useState("");

  const fetchData = useCallback(async (id) => {
    try {
      const response = await get(`/social/getSocial?id=${id}`);
      const responseData = response?.data?.data;
      const socialData = Array.isArray(responseData) ? responseData[0] : responseData;
      const fetchedData = socialData?.trainingAndDevelopment || {};

      // Get module from URL params, API response, or default to monthly
      const urlModule = searchParams.get("entryType");
      const apiModule = socialData?.module || socialData?._socialDataRef?.module;
      let finalModule = "monthly";

      if (urlModule === "annual" || apiModule === "Annual" || apiModule === "annual") {
        // Restrict access - this component is only for monthly entries
        setIsRestricted(true);
        return;
      } else if (urlModule === "monthly" || apiModule === "Monthly" || apiModule === "monthly") {
        finalModule = "monthly";
      }

      setModule(finalModule);
      setIsRestricted(false);

      // Get totalEmployees from employeeWellBeing
      const employeeWellBeing = socialData?.employeeWellBeing || {};
      console.log(employeeWellBeing.totalEmployees, "employeeWellBeing");
      const fetchedTotalEmployees = parseFloat(employeeWellBeing.totalEmployees) || 0;
      setTotalEmployees(fetchedTotalEmployees);

      // Only process monthly data (this component is monthly-only)
      const monthlyData = {};

      if (fetchedData) {
        // Monthly fields
        const monthlyFields = [
          "programsOffered",
          "budgetAllocated",
          "policyAwarenessTopics",
          "skillTrainings",
          "awarenessTrainings",
        ];

        Object.keys(fetchedData).forEach((key) => {
          if (monthlyFields.includes(key)) {
            monthlyData[key] = fetchedData[key];
          }
        });
      }

      // Update monthly form data
      setMonthlyFormData((prevState) => {
        const updatedData = { ...prevState };
        
        if (monthlyData.programsOffered) {
          updatedData.programsOffered = {
            ...monthlyData.programsOffered,
            status:
              monthlyData.programsOffered?.status === undefined
                ? true
                : monthlyData.programsOffered.status === "true"
                  ? true
                  : monthlyData.programsOffered.status === "false"
                    ? false
                    : monthlyData.programsOffered.status,
            noReason: monthlyData.programsOffered?.noReason || "",
          };
        }
        
        if (monthlyData.budgetAllocated) {
          updatedData.budgetAllocated = {
            ...monthlyData.budgetAllocated,
            amount: formatWithCommas(monthlyData?.budgetAllocated?.amount),
          };
        }
        
        if (monthlyData.policyAwarenessTopics) {
          // Handle backward compatibility - if old structure exists, convert to new structure
          if (Array.isArray(monthlyData.policyAwarenessTopics) && monthlyData.policyAwarenessTopics.length > 0) {
            // Check if it's old structure (array of objects with topic and documentUrl)
            if (monthlyData.policyAwarenessTopics[0].topic && !monthlyData.policyAwarenessTopics[0].letter) {
              // Convert old structure to new structure
              updatedData.policyAwarenessTopics = [{
                letter: "a",
                trainings: monthlyData.policyAwarenessTopics.map(item => ({
                  topic: item.topic,
                  employeesParticipated: "",
                  percentage: "",
                  documentUrl: item.documentUrl || "",
                })),
              }];
            } else {
              // New structure - ensure letters exist
              updatedData.policyAwarenessTopics = monthlyData.policyAwarenessTopics.map((item, index) => ({
                ...item,
                letter: item.letter || String.fromCharCode(97 + index),
              }));
            }
          }
        }
        
        if (monthlyData.skillTrainings) {
          // Check if it's the correct structure (object with trainings array)
          if (monthlyData.skillTrainings.trainings && Array.isArray(monthlyData.skillTrainings.trainings)) {
            // Correct structure - process it
            const fetchedTrainings = monthlyData.skillTrainings.trainings;
            
            // Ensure default topics are always present
            const defaultTopicsMap = new Map(defaultSkillTrainingTopics.map(t => [t.topic, t]));
            const fetchedTopicsMap = new Map(fetchedTrainings.map(t => [t.topic, t]));
            
            // Start with default topics, but use fetched data if it exists
            const mergedTrainings = defaultSkillTrainingTopics.map(defaultTopic => {
              const fetchedTopic = fetchedTopicsMap.get(defaultTopic.topic);
              if (fetchedTopic) {
                // Normalize documentUrl to ensure it's a string
                let normalizedDocumentUrl = "";
                if (fetchedTopic.documentUrl) {
                  if (typeof fetchedTopic.documentUrl === "string") {
                    normalizedDocumentUrl = fetchedTopic.documentUrl;
                  } else if (Array.isArray(fetchedTopic.documentUrl) && fetchedTopic.documentUrl.length > 0) {
                    normalizedDocumentUrl = typeof fetchedTopic.documentUrl[0] === "string" 
                      ? fetchedTopic.documentUrl[0] 
                      : fetchedTopic.documentUrl[0]?.url || fetchedTopic.documentUrl[0]?.documentUrl || "";
                  } else if (typeof fetchedTopic.documentUrl === "object" && fetchedTopic.documentUrl !== null) {
                    normalizedDocumentUrl = fetchedTopic.documentUrl.url || fetchedTopic.documentUrl.documentUrl || fetchedTopic.documentUrl.document || "";
                  }
                }
                
                // Preserve all fields from fetched topic, ensuring documentUrl is a string
                return {
                  ...defaultTopic,
                  ...fetchedTopic,
                  documentUrl: normalizedDocumentUrl,
                };
              }
              return { ...defaultTopic };
            });
            
            // Add any additional topics from fetched data that aren't in defaults
            fetchedTrainings.forEach(fetchedTopic => {
              if (!defaultTopicsMap.has(fetchedTopic.topic)) {
                mergedTrainings.push(fetchedTopic);
              }
            });
            
            updatedData.skillTrainings = {
              trainings: mergedTrainings,
            };
          } else if (Array.isArray(monthlyData.skillTrainings)) {
            // Old array structure (with letter) - convert to new structure
            let fetchedTrainings = [];
            if (monthlyData.skillTrainings.length > 0 && monthlyData.skillTrainings[0].trainings) {
              fetchedTrainings = monthlyData.skillTrainings[0].trainings;
            } else {
              // Old flat array structure
              fetchedTrainings = monthlyData.skillTrainings;
            }
            
            const defaultTopicsMap = new Map(defaultSkillTrainingTopics.map(t => [t.topic, t]));
            const fetchedTopicsMap = new Map(fetchedTrainings.map(t => [t.topic, t]));
            
            const mergedTrainings = defaultSkillTrainingTopics.map(defaultTopic => {
              const fetchedTopic = fetchedTopicsMap.get(defaultTopic.topic);
              if (fetchedTopic) {
                // Normalize documentUrl to ensure it's a string
                let normalizedDocumentUrl = "";
                if (fetchedTopic.documentUrl) {
                  if (typeof fetchedTopic.documentUrl === "string") {
                    normalizedDocumentUrl = fetchedTopic.documentUrl;
                  } else if (Array.isArray(fetchedTopic.documentUrl) && fetchedTopic.documentUrl.length > 0) {
                    normalizedDocumentUrl = typeof fetchedTopic.documentUrl[0] === "string" 
                      ? fetchedTopic.documentUrl[0] 
                      : fetchedTopic.documentUrl[0]?.url || fetchedTopic.documentUrl[0]?.documentUrl || "";
                  } else if (typeof fetchedTopic.documentUrl === "object" && fetchedTopic.documentUrl !== null) {
                    normalizedDocumentUrl = fetchedTopic.documentUrl.url || fetchedTopic.documentUrl.documentUrl || fetchedTopic.documentUrl.document || "";
                  }
                }
                
                // Preserve all fields from fetched topic, ensuring documentUrl is a string
                return {
                  ...defaultTopic,
                  ...fetchedTopic,
                  documentUrl: normalizedDocumentUrl,
                };
              }
              return { ...defaultTopic };
            });
            
            fetchedTrainings.forEach(fetchedTopic => {
              if (!defaultTopicsMap.has(fetchedTopic.topic)) {
                mergedTrainings.push(fetchedTopic);
              }
            });
            
            updatedData.skillTrainings = {
              trainings: mergedTrainings,
            };
          }
        }
        
        if (monthlyData.awarenessTrainings) {
          // Check if it's the correct structure (object with trainings array)
          if (monthlyData.awarenessTrainings.trainings && Array.isArray(monthlyData.awarenessTrainings.trainings)) {
            // Correct structure - process it
            const fetchedTrainings = monthlyData.awarenessTrainings.trainings;
            
            // Ensure default topics are always present
            const defaultTopicsMap = new Map(defaultAwarenessTrainingTopics.map(t => [t.topic, t]));
            const fetchedTopicsMap = new Map(fetchedTrainings.map(t => [t.topic, t]));
            
            // Start with default topics, but use fetched data if it exists
            const mergedTrainings = defaultAwarenessTrainingTopics.map(defaultTopic => {
              const fetchedTopic = fetchedTopicsMap.get(defaultTopic.topic);
              if (fetchedTopic) {
                // Normalize documentUrl to ensure it's a string
                let normalizedDocumentUrl = "";
                if (fetchedTopic.documentUrl) {
                  if (typeof fetchedTopic.documentUrl === "string") {
                    normalizedDocumentUrl = fetchedTopic.documentUrl;
                  } else if (Array.isArray(fetchedTopic.documentUrl) && fetchedTopic.documentUrl.length > 0) {
                    normalizedDocumentUrl = typeof fetchedTopic.documentUrl[0] === "string" 
                      ? fetchedTopic.documentUrl[0] 
                      : fetchedTopic.documentUrl[0]?.url || fetchedTopic.documentUrl[0]?.documentUrl || "";
                  } else if (typeof fetchedTopic.documentUrl === "object" && fetchedTopic.documentUrl !== null) {
                    normalizedDocumentUrl = fetchedTopic.documentUrl.url || fetchedTopic.documentUrl.documentUrl || fetchedTopic.documentUrl.document || "";
                  }
                }
                
                // Preserve all fields from fetched topic, ensuring documentUrl is a string
                return {
                  ...defaultTopic,
                  ...fetchedTopic,
                  documentUrl: normalizedDocumentUrl,
                };
              }
              return { ...defaultTopic };
            });
            
            // Add any additional topics from fetched data that aren't in defaults
            fetchedTrainings.forEach(fetchedTopic => {
              if (!defaultTopicsMap.has(fetchedTopic.topic)) {
                mergedTrainings.push(fetchedTopic);
              }
            });
            
            updatedData.awarenessTrainings = {
              trainings: mergedTrainings,
            };
          } else if (Array.isArray(monthlyData.awarenessTrainings)) {
            // Old array structure (with letter) - convert to new structure
            let fetchedTrainings = [];
            if (monthlyData.awarenessTrainings.length > 0 && monthlyData.awarenessTrainings[0].trainings) {
              fetchedTrainings = monthlyData.awarenessTrainings[0].trainings;
            } else {
              // Old flat array structure
              fetchedTrainings = monthlyData.awarenessTrainings;
            }
            
            const defaultTopicsMap = new Map(defaultAwarenessTrainingTopics.map(t => [t.topic, t]));
            const fetchedTopicsMap = new Map(fetchedTrainings.map(t => [t.topic, t]));
            
            const mergedTrainings = defaultAwarenessTrainingTopics.map(defaultTopic => {
              const fetchedTopic = fetchedTopicsMap.get(defaultTopic.topic);
              if (fetchedTopic) {
                // Normalize documentUrl to ensure it's a string
                let normalizedDocumentUrl = "";
                if (fetchedTopic.documentUrl) {
                  if (typeof fetchedTopic.documentUrl === "string") {
                    normalizedDocumentUrl = fetchedTopic.documentUrl;
                  } else if (Array.isArray(fetchedTopic.documentUrl) && fetchedTopic.documentUrl.length > 0) {
                    normalizedDocumentUrl = typeof fetchedTopic.documentUrl[0] === "string" 
                      ? fetchedTopic.documentUrl[0] 
                      : fetchedTopic.documentUrl[0]?.url || fetchedTopic.documentUrl[0]?.documentUrl || "";
                  } else if (typeof fetchedTopic.documentUrl === "object" && fetchedTopic.documentUrl !== null) {
                    normalizedDocumentUrl = fetchedTopic.documentUrl.url || fetchedTopic.documentUrl.documentUrl || fetchedTopic.documentUrl.document || "";
                  }
                }
                
                // Preserve all fields from fetched topic, ensuring documentUrl is a string
                return {
                  ...defaultTopic,
                  ...fetchedTopic,
                  documentUrl: normalizedDocumentUrl,
                };
              }
              return { ...defaultTopic };
            });
            
            fetchedTrainings.forEach(fetchedTopic => {
              if (!defaultTopicsMap.has(fetchedTopic.topic)) {
                mergedTrainings.push(fetchedTopic);
              }
            });
            
            updatedData.awarenessTrainings = {
              trainings: mergedTrainings,
            };
          }
        }
        
        return updatedData;
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchData(id);
  }, [id, fetchData]);


  const handleNestedBooleanChangeMonthly = (e) => {
    const { name, value } = e.target;
    const [parent, key] = name.split(".");
    setMonthlyFormData((prev) => {
      let updatedParent = {
        ...prev[parent],
        [key]: value === "true",
      };
      if (parent === "programsOffered" && key === "status") {
        if (value === "true") {
          updatedParent.noReason = "";
        } else if (typeof updatedParent.noReason !== "string") {
          updatedParent.noReason = "";
        }
      }
      if ("documentUrl" in updatedParent && updatedParent.status === false) {
        updatedParent.documentUrl = "";
      }
      return {
        ...prev,
        [parent]: updatedParent,
      };
    });
    setErrors((prev) => ({
      ...prev,
      [parent]: "",
    }));
  };

  const handleFileChange = (file, policy, index = null, type = null, tableIndex = null) => {
    // This component is monthly-only
    if (type === "skillTrainings" || type === "awarenessTrainings") {
      // Normalize file to ensure it's a string URL
      let normalizedFile = "";
      if (file) {
        if (typeof file === "string") {
          normalizedFile = file;
        } else if (Array.isArray(file) && file.length > 0) {
          normalizedFile = typeof file[0] === "string" ? file[0] : file[0]?.url || file[0]?.documentUrl || "";
        } else if (typeof file === "object" && file !== null) {
          normalizedFile = file.url || file.documentUrl || file.document || "";
        }
      }
      
      // Structure: { trainings: [...] }
      setMonthlyFormData((prev) => {
        const updatedTrainings = [...(prev[type]?.trainings || [])];
        updatedTrainings[index] = {
          ...updatedTrainings[index],
          documentUrl: normalizedFile,
        };
        return {
          ...prev,
          [type]: {
            trainings: updatedTrainings,
          },
        };
      });
      
      // Clear error for this specific field
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (newErrors[type]?.trainings?.[index]) {
          const updatedItemErrors = { ...newErrors[type].trainings[index] };
          delete updatedItemErrors.documentUrl;
          if (Object.keys(updatedItemErrors).length === 0) {
            // Remove the entire item error if no errors left
            const updatedTrainingsErrors = [...(newErrors[type].trainings || [])];
            updatedTrainingsErrors[index] = null;
            if (updatedTrainingsErrors.every(err => err === null)) {
              // Remove the entire trainings error if all items are valid
              delete newErrors[type];
            } else {
              newErrors[type] = { trainings: updatedTrainingsErrors };
            }
          } else {
            newErrors[type] = {
              ...newErrors[type],
              trainings: [...(newErrors[type].trainings || [])].map((err, idx) => 
                idx === index ? updatedItemErrors : err
              ),
            };
          }
        }
        return newErrors;
      });
    } else if (type === "policyAwarenessTopics") {
      setMonthlyFormData((prev) => {
        const updated = [...prev[type]];
        updated[tableIndex] = {
          ...updated[tableIndex],
          trainings: updated[tableIndex].trainings.map((item, idx) =>
            idx === index ? { ...item, documentUrl: file } : item
          ),
        };
        return {
          ...prev,
          [type]: updated,
        };
      });
    } else {
      // programsOffered
      setMonthlyFormData((prev) => ({
        ...prev,
        [policy]: {
          ...prev[policy],
         
        },
      }));
    }
    setErrors((prev) => ({
      ...prev,
      [policy]: "",
    }));
  };






  const handleAddPolicyAwarenessTable = () => {
    const letters = "abcdefghijklmnopqrstuvwxyz";
    const nextLetter = letters[monthlyFormData.policyAwarenessTopics.length];
    
    setMonthlyFormData((prev) => ({
      ...prev,
      policyAwarenessTopics: [
        ...prev.policyAwarenessTopics,
        {
          letter: nextLetter,
          trainings: [
            { topic: "Human Rights Policy Awareness", employeesParticipated: "", percentage: "", documentUrl: "" },
            { topic: "Prevention of Child Labour", employeesParticipated: "", percentage: "", documentUrl: "" },
            { topic: "Prevention of Forced / Involuntary Labour", employeesParticipated: "", percentage: "", documentUrl: "" },
            { topic: "Non-Discrimination & Equal Opportunity", employeesParticipated: "", percentage: "", documentUrl: "" },
            { topic: "Prevention of Sexual Harassment (POSH)", employeesParticipated: "", percentage: "", documentUrl: "" },
            { topic: "Responsible Marketing & Advertising", employeesParticipated: "", percentage: "", documentUrl: "" },
            { topic: "Consumer Grievance Handling", employeesParticipated: "", percentage: "", documentUrl: "" },
            { topic: "Customer Information Protection", employeesParticipated: "", percentage: "", documentUrl: "" },
          ],
        },
      ],
    }));
  };

  const handleDeletePolicyAwarenessTable = (tableIndex) => {
    if (monthlyFormData.policyAwarenessTopics.length > 1) {
      setMonthlyFormData((prev) => ({
        ...prev,
        policyAwarenessTopics: prev.policyAwarenessTopics.filter((_, index) => index !== tableIndex).map((item, index) => ({
          ...item,
          letter: String.fromCharCode(97 + index), // Reassign letters: a, b, c, etc.
        })),
      }));
    }
  };

  const handleTrainingDataChange = (e, index, trainingType) => {
    const { name, value } = e.target;
    const normalizedValue = String(value ?? "").replace(/,/g, "");
    
    // If percentage is being changed manually, validate it
    if (name === "percentage") {
      const numValue = normalizedValue === "" ? "" : Number(normalizedValue);
      if (numValue !== "" && (isNaN(numValue) || numValue < 0 || numValue > 100)) {
        return; // Don't update if invalid
      }
    }
    
    // Validate employeesParticipated - should not exceed totalEmployees
    if (name === "employeesParticipated") {
      const numValue = normalizedValue === "" ? "" : Number(normalizedValue);
      if (numValue !== "" && (isNaN(numValue) || numValue < 0 || numValue > totalEmployees)) {
        return; // Don't update if invalid or exceeds totalEmployees
      }
    }
    
    setMonthlyFormData((prev) => {
      const updatedTrainings = [...(prev[trainingType]?.trainings || [])];
      const currentTraining = updatedTrainings[index];
      
      let updatedItem = { ...currentTraining };
      
      if (name === "employeesParticipated") {
        // Auto-calculate percentage when employeesParticipated changes
        const employeesParticipated = normalizedValue === "" ? 0 : Number(normalizedValue);
        const calculatedPercentage = totalEmployees > 0 
          ? ((employeesParticipated / totalEmployees) * 100).toFixed(2)
          : "";
        
        updatedItem = {
          ...updatedItem,
          employeesParticipated:
            normalizedValue === ""
              ? ""
              : formatWithCommas(normalizedValue),
          percentage: calculatedPercentage === "" ? "" : parseFloat(calculatedPercentage),
        };
      } else if (name === "percentage") {
        // Allow manual percentage entry (though it will be overridden if employeesParticipated changes)
        updatedItem = {
          ...updatedItem,
          [name]: value === "" ? "" : Number(value),
        };
      } else {
        updatedItem = {
          ...updatedItem,
          [name]: value,
        };
      }
      
      updatedTrainings[index] = updatedItem;
      
      // Clear errors for this field when value is updated
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        if (newErrors[trainingType]?.trainings?.[index]) {
          const updatedItemErrors = { ...newErrors[trainingType].trainings[index] };
          delete updatedItemErrors[name];
          if (Object.keys(updatedItemErrors).length === 0) {
            // Remove the entire item error if no errors left
            const updatedTrainingsErrors = [...(newErrors[trainingType].trainings || [])];
            updatedTrainingsErrors[index] = null;
            if (updatedTrainingsErrors.every(err => err === null)) {
              // Remove the entire trainings error if all items are valid
              delete newErrors[trainingType];
            } else {
              newErrors[trainingType] = { trainings: updatedTrainingsErrors };
            }
          } else {
            newErrors[trainingType] = {
              ...newErrors[trainingType],
              trainings: [...(newErrors[trainingType].trainings || [])].map((err, idx) => 
                idx === index ? updatedItemErrors : err
              ),
            };
          }
        }
        return newErrors;
      });
      
      return {
        ...prev,
        [trainingType]: {
          trainings: updatedTrainings,
        },
      };
    });
  };

  const handleAddSkillTraining = () => {
    setMonthlyFormData((prev) => ({
      ...prev,
      skillTrainings: {
        trainings: [
          ...(prev.skillTrainings?.trainings || []),
          { topic: "", employeesParticipated: "", percentage: "", documentUrl: "" },
        ],
      },
    }));
  };

  const handleDeleteSkillTraining = (index) => {
    // Only allow deletion for rows beyond the predefined default topics
    if (index < defaultSkillTrainingTopics.length) return;

    setMonthlyFormData((prev) => ({
      ...prev,
      skillTrainings: {
        trainings: (prev.skillTrainings?.trainings || []).filter((_, idx) => idx !== index),
      },
    }));

    // Remove deleted row's validation errors and reindex remaining rows
    setErrors((prevErrors) => {
      const nextErrors = { ...prevErrors };
      const skillErrors = nextErrors.skillTrainings;
      const trainingsErrors = skillErrors?.trainings;

      if (!Array.isArray(trainingsErrors)) {
        return nextErrors;
      }

      const updatedTrainingsErrors = trainingsErrors.filter((_, idx) => idx !== index);
      const hasAnyError = updatedTrainingsErrors.some(
        (rowError) => rowError && Object.keys(rowError).length > 0
      );

      if (hasAnyError) {
        nextErrors.skillTrainings = {
          ...skillErrors,
          trainings: updatedTrainingsErrors,
        };
      } else {
        delete nextErrors.skillTrainings;
      }

      return nextErrors;
    });
  };

  const handleAddAwarenessTraining = () => {
    setMonthlyFormData((prev) => ({
      ...prev,
      awarenessTrainings: {
        trainings: [
          ...(prev.awarenessTrainings?.trainings || []),
          { topic: "", employeesParticipated: "", percentage: "", documentUrl: "" },
        ],
      },
    }));
  };

  const handleDeleteAwarenessTraining = (index) => {
    // Only allow deletion for rows beyond the predefined default topics
    if (index < defaultAwarenessTrainingTopics.length) return;

    setMonthlyFormData((prev) => ({
      ...prev,
      awarenessTrainings: {
        trainings: (prev.awarenessTrainings?.trainings || []).filter((_, idx) => idx !== index),
      },
    }));

    // Remove deleted row's validation errors and reindex remaining rows
    setErrors((prevErrors) => {
      const nextErrors = { ...prevErrors };
      const awarenessErrors = nextErrors.awarenessTrainings;
      const trainingsErrors = awarenessErrors?.trainings;

      if (!Array.isArray(trainingsErrors)) {
        return nextErrors;
      }

      const updatedTrainingsErrors = trainingsErrors.filter((_, idx) => idx !== index);
      const hasAnyError = updatedTrainingsErrors.some(
        (rowError) => rowError && Object.keys(rowError).length > 0
      );

      if (hasAnyError) {
        nextErrors.awarenessTrainings = {
          ...awarenessErrors,
          trainings: updatedTrainingsErrors,
        };
      } else {
        delete nextErrors.awarenessTrainings;
      }

      return nextErrors;
    });
  };

  const handleDelete = () => {
    if (deleteData.fieldName === "skillTrainings") {
      handleDeleteSkillTraining(Number(deleteData.index));
    }
    if (deleteData.fieldName === "awarenessTrainings") {
      handleDeleteAwarenessTraining(Number(deleteData.index));
    }
    setDeleteDialog(false);
    setDeleteData({ fieldName: "", index: "" });
  };

  const hasCustomSkillRows = (monthlyFormData.skillTrainings?.trainings || []).some(
    (_, idx) => idx >= defaultSkillTrainingTopics.length,
  );
  const hasCustomAwarenessRows = (
    monthlyFormData.awarenessTrainings?.trainings || []
  ).some((_, idx) => idx >= defaultAwarenessTrainingTopics.length);

  const validation = () => {
    let newErrors = {}; // Initialize an empty error object
    let hasErrors = false;

    const isEmpty = (value) =>
      value === "" || value === null || value === undefined;

    // Helper to check if a value has meaningful data
    const hasValue = (value) => {
      if (value === null || value === undefined) return false;
      if (typeof value === "string") return value.trim() !== "";
      if (typeof value === "number") return true; // 0 is a valid value
      if (typeof value === "boolean") return true;
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === "object") return Object.keys(value).length > 0;
      return true;
    };

    // Helper specifically for documentUrl:
    // Treat a non-empty string URL as valid. Other types are considered invalid.
    const hasDocumentUrl = (value) => {
      if (value === null || value === undefined) return false;
      if (typeof value === "string") return value.trim() !== "";
      return false;
    };

    // Validate programsOffered
    if (monthlyFormData.programsOffered?.status === undefined || monthlyFormData.programsOffered?.status === null) {
      newErrors.programsOffered = { status: textError };
      hasErrors = true;
    }

    // If "No", capture reason and skip i/ii validations
    if (monthlyFormData.programsOffered?.status === false) {
      if (isEmpty(monthlyFormData.programsOffered?.noReason)) {
        if (!newErrors.programsOffered) newErrors.programsOffered = {};
        newErrors.programsOffered.noReason = textError;
        hasErrors = true;
      }
      setErrors(newErrors);
      return hasErrors;
    }

    // Validate budgetAllocated
    if (isEmpty(monthlyFormData.budgetAllocated?.currency)) {
      if (!newErrors.budgetAllocated) newErrors.budgetAllocated = {};
      newErrors.budgetAllocated.currency = textError;
      hasErrors = true;
    }
    if (isEmpty(monthlyFormData.budgetAllocated?.amount)) {
      if (!newErrors.budgetAllocated) newErrors.budgetAllocated = {};
      newErrors.budgetAllocated.amount = textError;
      hasErrors = true;
    }

    // Validate skillTrainings
    if (!monthlyFormData.skillTrainings?.trainings || !Array.isArray(monthlyFormData.skillTrainings.trainings) || monthlyFormData.skillTrainings.trainings.length === 0) {
      newErrors.skillTrainings = { trainings: "At least one skill training is required" };
      hasErrors = true;
    } else {
      const skillTrainingsErrors = monthlyFormData.skillTrainings.trainings
        .map((item, index) => {
          const itemErrors = {};

          // Row is considered "active" if user has filled any field
          const hasAnyData =
            hasValue(item.topic) ||
            hasValue(item.employeesParticipated) ||
            (item.percentage !== "" &&
              item.percentage !== null &&
              item.percentage !== undefined) ||
            hasDocumentUrl(item.documentUrl);

          const isNewRow = index >= defaultSkillTrainingTopics.length;

          // For default rows: if completely empty -> ignore (no errors)
          if (!hasAnyData && !isNewRow) {
            return null;
          }

          // For newly added rows: even completely empty rows should show as errors
          if (!hasAnyData && isNewRow) {
            itemErrors.topic = textError;
            itemErrors.employeesParticipated = textError;
            itemErrors.percentage = textError;
            itemErrors.documentUrl = textError;
            return itemErrors;
          }

          // For active rows, all fields are mandatory
          if (!hasValue(item.topic)) {
            itemErrors.topic = textError;
          }
          if (!hasValue(item.employeesParticipated)) {
            itemErrors.employeesParticipated = textError;
          }
          if (
            item.percentage === "" ||
            item.percentage === null ||
            item.percentage === undefined
          ) {
            itemErrors.percentage = textError;
          }
          if (!hasDocumentUrl(item.documentUrl)) {
            itemErrors.documentUrl = textError;
          }

          return Object.keys(itemErrors).length > 0 ? itemErrors : null;
        });

      if (skillTrainingsErrors.some((error) => error !== null)) {
        newErrors.skillTrainings = { trainings: skillTrainingsErrors };
        hasErrors = true;
      }
    }

    // Validate awarenessTrainings
    if (!monthlyFormData.awarenessTrainings?.trainings || !Array.isArray(monthlyFormData.awarenessTrainings.trainings) || monthlyFormData.awarenessTrainings.trainings.length === 0) {
      newErrors.awarenessTrainings = { trainings: "At least one awareness training is required" };
      hasErrors = true;
    } else {
      const awarenessTrainingsErrors = monthlyFormData.awarenessTrainings.trainings
        .map((item, index) => {
          const itemErrors = {};

          // Row is considered "active" if user has filled any field
          const hasAnyData =
            hasValue(item.topic) ||
            hasValue(item.employeesParticipated) ||
            (item.percentage !== "" &&
              item.percentage !== null &&
              item.percentage !== undefined) ||
            hasDocumentUrl(item.documentUrl);

          const isNewRow = index >= defaultAwarenessTrainingTopics.length;

          // For default rows: if completely empty -> ignore (no errors)
          if (!hasAnyData && !isNewRow) {
            return null;
          }

          // For newly added rows: even completely empty rows should show as errors
          if (!hasAnyData && isNewRow) {
            itemErrors.topic = textError;
            itemErrors.employeesParticipated = textError;
            itemErrors.percentage = textError;
            itemErrors.documentUrl = textError;
            return itemErrors;
          }

          // For active rows, all fields are mandatory
          if (!hasValue(item.topic)) {
            itemErrors.topic = textError;
          }
          if (!hasValue(item.employeesParticipated)) {
            itemErrors.employeesParticipated = textError;
          }
          // Percentage can be 0, so check only for empty / missing
          if (
            item.percentage === "" ||
            item.percentage === null ||
            item.percentage === undefined
          ) {
            itemErrors.percentage = textError;
          }
          if (!hasDocumentUrl(item.documentUrl)) {
            itemErrors.documentUrl = textError;
          }

          return Object.keys(itemErrors).length > 0 ? itemErrors : null;
        });

      if (awarenessTrainingsErrors.some((error) => error !== null)) {
        newErrors.awarenessTrainings = { trainings: awarenessTrainingsErrors };
        hasErrors = true;
      }
    }

    console.log(newErrors,monthlyFormData, "newErrors");
    setErrors(newErrors);
    return hasErrors;
  };

  const handleSubmit = async () => {
    if (validation()) return;

    const normalizedProgramsOffered = {
      ...(monthlyFormData?.programsOffered || {}),
      status: Boolean(monthlyFormData?.programsOffered?.status),
      noReason:
        monthlyFormData?.programsOffered?.status === false
          ? String(monthlyFormData?.programsOffered?.noReason || "").trim()
          : "",
    };

    // Only submit monthly data (this component is monthly-only)
    const payload = {
      trainingAndDevelopment: {
        ...stripCommasFromNumericStrings(monthlyFormData),
        programsOffered: normalizedProgramsOffered,
      },
      stepsCompleted: {
        trainingAndDevelopment: true,
      },
    };

    await put(`/social/updateSocial?Id=${id}`, payload)
      .then((res) => {
        navigate(`/social/data/customer-satisfaction/${id}`);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  let list = ["USD", "INR", "YEN", "EURO", "AUD"];

  return (
    <MainDashboardLayout sidebar={<SocialLayout />}>
      <DeleteModal
        onOpen={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        onConfirm={handleDelete}
      />
      {/* <div style={{ padding: "1rem 0" }}>
        <span
          style={{
            fontSize: "14px",
            fontWeight: "400",
            color: "rgba(0, 0, 0, 0.8)",
          }}
        >
          Data{" "}
        </span>
        <span>
          <ChevronRightIcon />
        </span>{" "}
        <span
          style={{
            fontSize: "14px",
            fontWeight: "600",
            color: "rgba(0, 0, 0, 1)",
          }}
        >
          Add New Entry
        </span>
      </div>
      <div
        style={{
          fontSize: "24px",
          fontWeight: "700",
          color: "#001632",
          marginBottom: "1rem",
        }}
      >
        Add New Entry
      </div> */}
      <p className={styles.heading + " " + styles.form_headingStrongUpper}>G. TRAINING AND DEVELOPMENT</p>

      {/* Restrict access for annual entries */}
      {isRestricted ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "400px",
            textAlign: "center",
            padding: "2rem",
          }}
        >
          <p style={{ fontSize: "18px", fontWeight: "bold", color: "#D34040", marginBottom: "1rem" }}>
            Access Restricted
          </p>
          <p style={{ fontSize: "14px", color: "rgba(0, 0, 0, 0.7)", marginBottom: "2rem" }}>
            This page is only available for Monthly entries. Please use the Monthly entry type to access Training and Development.
          </p>
          <Button
            variant="contained"
            onClick={() => navigate(`/social/data/human-rights/${id}`)}
            sx={{
              textTransform: "none",
              backgroundColor: "#1C486B",
              "&:hover": {
                backgroundColor: "#153d5a",
              },
            }}
          >
            Go Back
          </Button>
        </Box>
      ) : (
        <>
          {/* Monthly Module */}
          {module === "monthly" && (
            <>
              {/* 1. Training and Development Programs */}
              <BooleanInputField
                label="1. Training and Development Programs:"
                name="programsOffered.status"
                value={monthlyFormData?.programsOffered?.status ?? true}
                onChange={handleNestedBooleanChangeMonthly}
              />

              {monthlyFormData?.programsOffered?.status === false && (
                <div style={{ width: "100%", marginTop: "0.75rem", marginBottom: "0.75rem" }}>
                     <p
                  className={styles.form_heading}
                  style={{ fontWeight: "bold",
                    //  marginTop: "1rem"
                     }}
                >
                  Additional Details
                </p>
                  <TextField
                    name="programsOffered.noReason"
                    value={monthlyFormData?.programsOffered?.noReason || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setMonthlyFormData((prev) => ({
                        ...prev,
                        programsOffered: {
                          ...prev.programsOffered,
                          noReason: value,
                        },
                      }));
                      setErrors((prev) => ({
                        ...prev,
                        programsOffered: {
                          ...prev?.programsOffered,
                          noReason: "",
                        },
                      }));
                    }}
                    variant="outlined"
                    size="small"
                    
                    multiline
                    minRows={1}
                    placeholder="Enter text for additional"
                    error={!!errors?.programsOffered?.noReason}
                    helperText={errors?.programsOffered?.noReason}
                    sx={{ width: "50%" }}
                    FormHelperTextProps={{
                      sx: {
                        color: "#D34040",
                        marginLeft: 0,
                        fontSize: "14px",
                        paddingLeft: "1px",
                        width:"100%",
                      },
                    }}
                  />
                </div>
              )}

              {monthlyFormData?.programsOffered?.status !== false && (
                <>
                  {/* 2. Budget Allocated for Training and Development */}
                  <div style={{ width: "100%" }}>
                    <p className={styles.form_heading} >
                      i. Budget Allocated for Training and Development:
                    </p>
                    <Box sx={{ display: "flex", gap: "1rem", pl: 2, mt: 1 }}>
                      <div style={{ width: "50%" }}>
                        <p className={styles.form_heading}>Currency</p>
                        <FormControl
                          fullWidth
                          size="small"
                          error={!!errors?.budgetAllocated?.currency}
                        >
                          <Select
                            name="currency"
                            value={monthlyFormData.budgetAllocated.currency || ""}
                            onChange={(e) => {
                              setMonthlyFormData((prev) => ({
                                ...prev,
                                budgetAllocated: {
                                  ...prev.budgetAllocated,
                                  currency: e.target.value,
                                },
                              }));
                              setErrors((prev) => ({
                                ...prev,
                                budgetAllocated: {
                                  ...prev?.budgetAllocated,
                                  currency: "",
                                },
                              }));
                            }}
                            size="small"
                            displayEmpty
                            sx={{ backgroundColor: "#EDEDED" }}
                          >
                            <MenuItem value="" disabled>
                              Tap to select
                            </MenuItem>
                            {list.map((item, index) => (
                              <MenuItem key={index} value={item}>
                                {item}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors?.budgetAllocated?.currency && (
                            <FormHelperText sx={{ color: "#D34040", marginLeft: "0rem", fontSize: "14px" }}>
                              {errors.budgetAllocated.currency}
                            </FormHelperText>
                          )}
                        </FormControl>
                      </div>
                      <div style={{ width: "50%" }}>
                        <p className={styles.form_heading}>Amount</p>
                        <TextField
                          name="amount"
                          value={formatWithCommas(
                            monthlyFormData.budgetAllocated.amount,
                          )}
                          onChange={(e) => {
                            const rawValue = e.target.value.replace(/,/g, "");
                            if (!/^\d*$/.test(rawValue)) return;
                            setMonthlyFormData((prev) => ({
                              ...prev,
                              budgetAllocated: {
                                ...prev.budgetAllocated,
                                amount: rawValue ? Number(rawValue) : "",
                              },
                            }));
                            setErrors((prev) => ({
                              ...prev,
                              budgetAllocated: {
                                ...prev?.budgetAllocated,
                                amount: "",
                              },
                            }));
                          }}
                          variant="outlined"
                          size="small"
                          fullWidth
                          placeholder="Enter Number"
                          error={!!errors?.budgetAllocated?.amount}
                          helperText={errors?.budgetAllocated?.amount}
                          FormHelperTextProps={{
                            sx: {
                              color: "#D34040",
                              marginLeft: 0,
                              fontSize: "14px",
                              paddingLeft: "1px",
                            },
                          }}
                        />
                      </div>
                    </Box>
                  </div>

                  {/* 3. Training topics covered: (a) Skill Trainings */}
                  <div style={{ marginTop: "1rem", marginBottom: "1rem" }}>
                <p className={styles.form_heading} >
                  ii. Training topics covered: 
                </p>
                <p className={styles.form_heading} style={{ fontWeight: "bold", marginTop: "1rem" }}>
                  (a) Skill Trainings
                </p>
                <TableContainer component={Paper} sx={{ marginTop: "10px", boxShadow: "none" }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            backgroundColor: "#1C486B",
                            color: "white",
                            fontWeight: "bold",
                            fontSize: "14px",
                            padding: "8px 16px",
                            borderRight: "1px solid rgba(255, 255, 255, 0.3)",
                            minWidth: "260px",
                            maxWidth: "360px",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                          }}
                        >
                          Skill Trainings
                        </TableCell>
                        <TableCell sx={{ backgroundColor: "#1C486B", color: "white", fontWeight: "bold", fontSize: "14px", padding: "8px 16px", borderRight: "1px solid rgba(255, 255, 255, 0.3)" }}>
                          Number of Employee Participated
                        </TableCell>
                        <TableCell sx={{ backgroundColor: "#1C486B", color: "white", fontWeight: "bold", fontSize: "14px", padding: "8px 16px", borderRight: "1px solid rgba(255, 255, 255, 0.3)" }}>
                          Percentage
                        </TableCell>
                        <TableCell sx={{ backgroundColor: "#1C486B", color: "white", fontWeight: "bold", fontSize: "14px", padding: "8px 16px" }}>
                          Upload Document
                        </TableCell>
                        {hasCustomSkillRows && (  <TableCell sx={{ backgroundColor: "#1C486B", color: "white", fontWeight: "bold", fontSize: "14px", padding: "8px 16px", width: "50px" }}>
                          Action
                        </TableCell>)}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(monthlyFormData.skillTrainings?.trainings || []).map((item, index) => (
                        <TableRow key={index}>
                          <TableCell
                            sx={{
                              padding: "8px 16px",
                              borderRight: "1px solid #E0E0E0",
                              minWidth: "260px",
                              maxWidth: "360px",
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                            }}
                          >
                            <TextField
                              name="topic"
                              value={item.topic || ""}
                              onChange={(e) => handleTrainingDataChange(e, index, "skillTrainings")}
                              variant="outlined"
                              size="small"
                              sx={{ 
                                width: "100%", 
                                backgroundColor: "#FFFFFF",
                                "& .MuiOutlinedInput-notchedOutline": {
                                  border: "none",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  border: errors?.skillTrainings?.trainings?.[index]?.topic ? "1px solid #D34040" : "none",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                  border: "none",
                                },
                                "& .MuiInputBase-input": {
                                  whiteSpace: "normal",
                                  wordBreak: "break-word",
                                },
                              }}
                              multiline
                              maxRows={3}
                              placeholder="Enter topic"
                            />
                          </TableCell>
                          <TableCell sx={{ padding: "8px 16px", borderRight: "1px solid #E0E0E0" }}>
                            <TextField
                              name="employeesParticipated"
                              value={item.employeesParticipated || ""}
                              onChange={(e) => handleTrainingDataChange(e, index, "skillTrainings")}
                              variant="outlined"
                              size="small"
                              sx={{ 
                                width: "100%", 
                                backgroundColor: "#FFFFFF",
                                "& .MuiOutlinedInput-notchedOutline": {
                                  border: "none",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  border: "none",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                  border: "none",
                                },
                              }}
                              placeholder="Enter Number"
                            />
                          </TableCell>
                          <TableCell sx={{ padding: 0, borderRight: "1px solid #E0E0E0",backgroundColor: "#F5F5F5" }}>
                            <TextField
                              name="percentage"
                              value={item.percentage || ""}
                              onChange={(e) => handleTrainingDataChange(e, index, "skillTrainings")}
                              variant="outlined"
                              size="small"
                              disabled
                              fullWidth
                              sx={{ 
                                backgroundColor: "#F5F5F5",
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 0,
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                  border: "none",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  border: "none",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                  border: "none",
                                },
                                "& .MuiInputBase-input": {
                                  padding: "8px 16px",
                                },
                              }}
                              placeholder="Auto-calculated"
                            />
                          </TableCell>
                          <TableCell sx={{ padding: "8px 16px" }}>
                            <FileUpload
                              setDocumentUrl={(file) => handleFileChange(file, "skillTrainings", index, "skillTrainings")}
                              filesList={item.documentUrl}
                              isDisabled={true}
                            />
                          </TableCell>
                          {hasCustomSkillRows && (
                            <TableCell sx={{ padding: "8px 16px", width: "50px" }}>
                              {index >= defaultSkillTrainingTopics.length && (
                                <IconButton
                                  onClick={() => {
                                    setDeleteDialog(true);
                                    setDeleteData({ fieldName: "skillTrainings", index });
                                  }}
                                  sx={{
                                    color: "#D34040",
                                    padding: "4px",
                                    "&:hover": {
                                      backgroundColor: "#FFEBEE",
                                    },
                                  }}
                                >
                                  <DeleteIcon sx={{ fontSize: "20px" }} />
                                </IconButton>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {/* Add button for Skill Trainings */}
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "10px" }}>
                  <Button
                    variant="outlined"
                    onClick={handleAddSkillTraining}
                    sx={{
                      textTransform: "none",
                      minWidth: "auto",
                      padding: "6px 12px",
                      whiteSpace: "nowrap",
                      borderColor: "#1C486B",
                      color: "#1C486B",
                      backgroundColor: "#FFFFFF",
                    }}
                  >
                    Add +
                  </Button>
                </div>
                {/* Error messages for Skill Trainings */}
                {errors?.skillTrainings?.trainings && (
                  <div style={{ marginTop: "10px", padding: "0 1rem" }}>
                    <FormHelperText sx={{ color: "#D34040", fontSize: "14px", marginTop: "5px" }}>
                      <strong>Skill Trainings - Missing fields:</strong>
                      {Array.isArray(errors.skillTrainings.trainings) ? (
                        <ul style={{ margin: "5px 0", paddingLeft: "20px" }}>
                          {errors.skillTrainings.trainings.map((itemErrors, index) => {
                            if (!itemErrors || typeof itemErrors !== "object") return null;
                            const emptyFields = Object.keys(itemErrors)
                              .filter(key => itemErrors[key])
                              .map(key => {
                                const fieldNames = {
                                  topic: "Topic",
                                  employeesParticipated: "Number of Employees Participated",
                                  percentage: "Percentage",
                                  documentUrl: "Document"
                                };
                                return fieldNames[key] || key;
                              });
                            if (emptyFields.length === 0) return null;
                            return (
                              <li key={index} style={{ marginBottom: "3px" }}>
                                Row {index + 1}: {emptyFields.join(", ")}
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <span style={{ marginLeft: "5px" }}>{errors.skillTrainings.trainings}</span>
                      )}
                    </FormHelperText>
                  </div>
                )}
                  </div>

                  {/* (a) Awareness Training */}
                  <div style={{ marginTop: "1rem", marginBottom: "1rem" }}>
                <p className={styles.form_heading} style={{ fontWeight: "bold", marginTop: "1rem" }}>
                  (b) Awareness Training
                </p>
                <TableContainer component={Paper} sx={{ marginTop: "10px", boxShadow: "none" }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            backgroundColor: "#1C486B",
                            color: "white",
                            fontWeight: "bold",
                            fontSize: "14px",
                            padding: "8px 16px",
                            borderRight: "1px solid rgba(255, 255, 255, 0.3)",
                            minWidth: "260px",
                            maxWidth: "360px",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                          }}
                        >
                          Awareness Training
                        </TableCell>
                        <TableCell sx={{ backgroundColor: "#1C486B", color: "white", fontWeight: "bold", fontSize: "14px", padding: "8px 16px", borderRight: "1px solid rgba(255, 255, 255, 0.3)" }}>
                          Number of Employee Participated
                        </TableCell>
                        <TableCell sx={{ backgroundColor: "#1C486B", color: "white", fontWeight: "bold", fontSize: "14px", padding: "8px 16px", borderRight: "1px solid rgba(255, 255, 255, 0.3)" }}>
                          Percentage
                        </TableCell>
                        <TableCell sx={{ backgroundColor: "#1C486B", color: "white", fontWeight: "bold", fontSize: "14px", padding: "8px 16px" }}>
                          Upload Document
                        </TableCell>
                        {hasCustomAwarenessRows && ( <TableCell sx={{ backgroundColor: "#1C486B", color: "white", fontWeight: "bold", fontSize: "14px", padding: "8px 16px", width: "50px" }}>
                          Action
                        </TableCell>)}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(monthlyFormData.awarenessTrainings?.trainings || []).map((item, index) => (
                        <TableRow key={index}>
                          <TableCell
                            sx={{
                              padding: "8px 16px",
                              borderRight: "1px solid #E0E0E0",
                              minWidth: "260px",
                              maxWidth: "360px",
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                            }}
                          >
                            <TextField
                              name="topic"
                              value={item.topic || ""}
                              onChange={(e) => handleTrainingDataChange(e, index, "awarenessTrainings")}
                              variant="outlined"
                              size="small"
                              sx={{ 
                                width: "100%", 
                                backgroundColor: "#FFFFFF",
                                "& .MuiOutlinedInput-notchedOutline": {
                                  border: "none",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  border: "none",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                  border: "none",
                                },
                              }}
                              multiline
                              maxRows={3}
                              inputProps={{
                                style: { whiteSpace: "normal", wordBreak: "break-word" },
                              }}
                              placeholder="Enter topic"
                            />
                          </TableCell>
                          <TableCell sx={{ padding: "8px 16px", borderRight: "1px solid #E0E0E0" }}>
                            <TextField
                              name="employeesParticipated"
                              value={item.employeesParticipated || ""}
                              onChange={(e) => handleTrainingDataChange(e, index, "awarenessTrainings")}
                              variant="outlined"
                              size="small"
                              sx={{ 
                                width: "100%", 
                                backgroundColor: "#FFFFFF",
                                "& .MuiOutlinedInput-notchedOutline": {
                                  border: "none",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  border: "none",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                  border: "none",
                                },
                              }}
                              placeholder="Enter Number"
                            />
                          </TableCell>
                          <TableCell sx={{ padding: 0, borderRight: "1px solid #E0E0E0",backgroundColor: "#F5F5F5" }}>
                            <TextField
                              name="percentage"
                              value={item.percentage || ""}
                              onChange={(e) => handleTrainingDataChange(e, index, "awarenessTrainings")}
                              variant="outlined"
                              size="small"
                              disabled
                              fullWidth
                              sx={{ 
                                backgroundColor: "#F5F5F5",
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 0,
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                  border: "none",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                  border: "none",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                  border: "none",
                                },
                                "& .MuiInputBase-input": {
                                  padding: "8px 16px",
                                },
                              }}
                              placeholder="Auto-calculated"
                            />
                          </TableCell>
                          <TableCell sx={{ padding: "8px 16px" }}>
                            <FileUpload
                              setDocumentUrl={(file) => handleFileChange(file, "awarenessTrainings", index, "awarenessTrainings")}
                              filesList={item.documentUrl}
                              isDisabled={true}
                            />
                          </TableCell>
                          {hasCustomAwarenessRows && (
                            <TableCell sx={{ padding: "8px 16px", width: "50px" }}>
                              {index >= defaultAwarenessTrainingTopics.length && (
                                <IconButton
                                  onClick={() => {
                                    setDeleteDialog(true);
                                    setDeleteData({
                                      fieldName: "awarenessTrainings",
                                      index,
                                    });
                                  }}
                                  sx={{
                                    color: "#D34040",
                                    padding: "4px",
                                    "&:hover": {
                                      backgroundColor: "#FFEBEE",
                                    },
                                  }}
                                >
                                  <DeleteIcon sx={{ fontSize: "20px" }} />
                                </IconButton>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {/* Add button for Awareness Training */}
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "10px" }}>
                  <Button
                    variant="outlined"
                    onClick={handleAddAwarenessTraining}
                    sx={{
                      textTransform: "none",
                      minWidth: "auto",
                      padding: "6px 12px",
                      whiteSpace: "nowrap",
                      borderColor: "#1C486B",
                      color: "#1C486B",
                      backgroundColor: "#FFFFFF",
                    }}
                  >
                    Add +
                  </Button>
                </div>
                {/* Error messages for Awareness Training */}
                {errors?.awarenessTrainings?.trainings && (
                  <div style={{ marginTop: "10px", padding: "0 1rem" }}>
                    <FormHelperText sx={{ color: "#D34040", fontSize: "14px", marginTop: "5px" }}>
                      <strong>Awareness Trainings - Missing fields:</strong>
                      {Array.isArray(errors.awarenessTrainings.trainings) ? (
                        <ul style={{ margin: "5px 0", paddingLeft: "20px" }}>
                          {errors.awarenessTrainings.trainings.map((itemErrors, index) => {
                            if (!itemErrors || typeof itemErrors !== "object") return null;
                            const emptyFields = Object.keys(itemErrors)
                              .filter((key) => itemErrors[key])
                              .map((key) => {
                                const fieldNames = {
                                  topic: "Topic",
                                  employeesParticipated: "Number of Employees Participated",
                                  percentage: "Percentage",
                                  documentUrl: "Document",
                                };
                                return fieldNames[key] || key;
                              });
                            if (emptyFields.length === 0) return null;
                            return (
                              <li key={index} style={{ marginBottom: "3px" }}>
                                Row {index + 1}: {emptyFields.join(", ")}
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <span style={{ marginLeft: "5px" }}>{errors.awarenessTrainings.trainings}</span>
                      )}
                    </FormHelperText>
                  </div>
                )}
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}

      {!isRestricted && (
        <Box
          sx={{
            width: "100%",
            mt: "10%",
            mb: 5,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button
            variant="outlined"
            sx={backButtonSx}
            onClick={() => navigate(`/social/data/human-rights/${id}`)}
          >
            <WestIcon sx={{ mr: 2 }} /> Back
          </Button>
          <Button
            variant="contained"
            className={styles.submit_button}
            onClick={handleSubmit}
            sx={primaryButtonSx}
          >
            Save & Continue <ArrowForwardIcon sx={{ ml: 1 }} />
          </Button>
        </Box>
      )}
    </MainDashboardLayout>
  );
};

export default TrainingAndDevelopment;
